"use client";

import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import dynamic from "next/dynamic";

// Lazy load chart for expanded outcome view (closed events)
const MarketChart = dynamic(() => import("@/app/market/[slug]/components/MarketChart"), { ssr: false });
// Lazy load tabs panel for the active-event inline accordion — avoids shipping
// Orderbook/Trades/Holders code on first paint until the user picks an outcome.
const OutcomeDetailPanel = dynamic(
  () => import("./OutcomeDetailPanel").then((m) => m.OutcomeDetailPanel),
  { ssr: false }
);
import { useQuery, useQueryClient } from "@tanstack/react-query";
import CdnImage from "@/components/CdnImage";
import { getCountryFlagUrl } from "@/lib/country-data";
import { MarketNetworkAvatars } from "@/app/components/NetworkBetsSection";
import { sourceUrl, type Source } from "@/lib/source-url";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";
import { useMarketsRewardsParams } from "@/hooks/useMarketRewardsParams";
import { Gift } from "lucide-react";
import Tooltip from "@/app/components/Tooltip";
import { useT } from "@/hooks/useT";
import { formatCurrency } from "@/lib/format";
import type { EventData } from "@/hooks/useEventQuery";
import type {
  Market,
  OpenTradingModalFn,
  GetLivePriceFn,
  GetLiveBuyPricesFn,
  FormatVolumeFn,
} from "./types";
import { formatPriceCents, getOutcomeAbbr } from "./types";

// User position for a market outcome
interface UserPosition {
  tokenId: string;
  conditionId: string;
  marketSlug: string;
  outcome: string;
  shares: number;
  avgPrice: number;
}

// Open limit order for a market
interface UserOpenOrder {
  market: string; // conditionId or negRiskMarketID
  asset_id: string; // tokenId — reliable for matching
  side: string;
  outcome: string;
  price: number;
  size: number;
}

function isWorldCupEvent(slug: string, title: string): boolean {
  const combined = `${slug} ${title}`.toLowerCase();
  return combined.includes("world cup") && combined.includes("winner");
}

// Shared team-logos map (already powering Polymarket sports cards on /markets).
// Cached app-wide via React Query so multiple components reuse the same payload.
function useTeamLogos() {
  return useQuery<Record<string, string>>({
    queryKey: ["sports-live", "team-logos"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/sports-live/team-logos");
        if (!res.ok) return {};
        const data = await res.json();
        return (data?.logos || {}) as Record<string, string>;
      } catch {
        return {};
      }
    },
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
  });
}

function lookupTeamLogo(
  logos: Record<string, string>,
  teamName: string | null | undefined
): string | null {
  if (!teamName || !logos) return null;
  const key = teamName.trim().toLowerCase();
  if (!key) return null;
  if (logos[key]) return logos[key];
  // Try matching against league-scoped keys ("league:teamname")
  const suffix = `:${key}`;
  for (const k of Object.keys(logos)) {
    if (k.endsWith(suffix)) return logos[k];
  }
  return null;
}

interface EventMarketsGridProps {
  markets: Market[];
  source: Source;
  eventData: EventData;
  selectedMarketId: string | null;
  selectedMarket: Market | null;
  /** Preselected outcome ("yes" | "no") from the URL — highlights the matching
   *  YES/NO button on the selected market's row. */
  initialOutcome?: string | null;
  showAllMarkets: boolean;
  setShowAllMarkets: (v: boolean) => void;
  onSelectMarket: (marketId: string) => void;
  /** Click on YES/NO buttons — selects the market AND the outcome side in
   *  the event sidebar's TradingPanel. Replaces the previous TradingModal
   *  open on the event page (sidebar-only, non-blocking). */
  onSelectOutcome?: (marketIdentifier: string, outcomeIndex: 0 | 1) => void;
  /** Mobile-only: tap on a market card body opens a full-screen overlay
   *  instead of the inline accordion. Desktop layout is unaffected. */
  onSelectMarketMobile?: (marketIdentifier: string) => void;
  /** When set, the grid treats this market as explicitly collapsed by the
   * user — skips the selectedMarketId fallback for this id so the accordion
   * stays closed even though it's still selected on the right sidebar. */
  userCollapsedMarketId?: string | null;
  /** Controls which row has its inline OutcomeDetailPanel expanded. Kept
   * separate from `selectedMarketId` so that tapping YES/NO pills updates
   * the sidebar selection without opening/closing the panel. */
  expandedMarketId?: string | null;
  /** Suppress Smart Money / Open Interest / Volume / Who's Trading tabs
   * in the outcome detail panel. Used for short-lived markets (PM daily
   * crypto price-level events) where those tabs have no history. */
  hideAdvancedTabs?: boolean;
  getLivePrice: GetLivePriceFn;
  getLiveBuyPrices: GetLiveBuyPricesFn;
  openTradingModal: OpenTradingModalFn;
  formatVolume: FormatVolumeFn;
  networkActivity: any;
  getTradersForMarket: (activity: any, slug: string) => any[];
  currentCryptoPrice?: number | null;
  currentStrikeMarketId?: string;
  totalMarketsCount?: number;
}

export default function EventMarketsGrid({
  markets,
  source,
  eventData,
  selectedMarketId,
  selectedMarket,
  initialOutcome,
  showAllMarkets,
  setShowAllMarkets,
  onSelectMarket,
  onSelectOutcome,
  onSelectMarketMobile,
  expandedMarketId,
  userCollapsedMarketId,
  hideAdvancedTabs,
  getLivePrice,
  getLiveBuyPrices,
  openTradingModal,
  formatVolume,
  networkActivity,
  getTradersForMarket,
  currentCryptoPrice,
  currentStrikeMarketId,
  totalMarketsCount,
}: EventMarketsGridProps) {
  const t = useT();
  // Normalize preselected outcome to lowercase for matching ("yes" / "no")
  const preselectedOutcome = initialOutcome?.toLowerCase() || null;
  const { data: teamLogos = {} } = useTeamLogos();

  // For closed events, show all resolved markets so the user can still
  // see the final outcome split (winner / loser, settled prices). The
  // previous filter required `hasValidChartData` (Polymarket clobTokenIds)
  // which dropped every Kalshi/Predict closed market because those
  // sources don't expose CLOB token ids — leaving the user with an empty
  // grid on a closed event. Chart visibility is now decided per-row at
  // render time instead of as a hard list filter.
  const eventClosed = eventData?.closed;
  // When the event is closed: show every market in the payload.
  //
  // Previously the closed branch filtered to `m.closed === true`,
  // which mishandled the brief window after a 5m crypto up/down
  // expires: our local cascade flips `eventClosed` true the moment
  // endDate is reached, but Polymarket's Gamma response can still
  // carry `m.closed: false` for ~30-60s while the oracle settles. The
  // intersection produced an empty grid — the user lost sight of the
  // outcomes (and the row their position sits on) until refresh. Now
  // we keep the markets visible through that gap; the per-row UI
  // (`Result` column, win/lose tints) is already source-of-truth on
  // resolution status.
  const displayMarkets = eventClosed
    ? markets
    : markets.filter((m) => m.active && !m.closed);

  // Bulk fetch 24h hourly candlesticks for each market — one HTTP
  // request per event, O(1) lookup per row. Polymarket only (Predexon).
  const conditionIds = useMemo(
    () => displayMarkets.map((m) => (m as any).conditionId).filter(Boolean) as string[],
    [displayMarkets]
  );
  // Rewards per market — one /api/polymarket/rewards batch call that hits
  // CLOB directly with `sponsored=true` for each conditionId (same request
  // Polymarket's own frontend fires). Drives the gift icon next to spread.
  const { data: rewardsMap } = useMarketsRewardsParams(
    conditionIds,
    source === 'polymarket'
  );

  // Fetch user positions to show "You own" badge (Polymarket + Predict.fun + Kalshi)
  const { isLoggedIn } = useAuth();
  const queryClient = useQueryClient();
  // Counter the trade-event handler bumps so the next few refetches
  // skip the backend's PM positions Redis cache. Without this the user's
  // freshly-bought "You own" badge wouldn't appear for ~5s after a trade
  // because the backend would still be serving a pre-trade snapshot
  // captured before PM's data-api had indexed the new position.
  const bypassPositionsCacheRef = useRef(0);
  const { data: userPositions = [] } = useQuery<UserPosition[]>({
    queryKey: ["user-positions-event", source],
    queryFn: async () => {
      if (source === "polymarket") {
        const fresh = bypassPositionsCacheRef.current > 0;
        if (fresh) bypassPositionsCacheRef.current--;
        const url = `/api/portfolio/positions?status=open${fresh ? '&fresh=1' : ''}`;
        const res = await authFetch(url);
        if (!res.ok) return [];
        const data = await res.json();
        return (data.positions || []).map((p: any) => ({
          tokenId: p.tokenId,
          conditionId: p.conditionId,
          marketSlug: p.marketSlug,
          outcome: p.outcome,
          shares: p.shares,
          avgPrice: p.avgPrice,
        }));
      } else if (source === "predict") {
        const res = await authFetch("/api/trading/predict/positions");
        if (!res.ok) return [];
        const data = await res.json();
        return (data.positions || []).filter((p: any) => p.isOpen && p.size > 0.001).map((p: any) => ({
          tokenId: p.marketId,  // Predict uses marketId
          conditionId: p.conditionId || p.marketId,  // Fallback to marketId for matching
          marketSlug: p.marketSlug,
          outcome: p.outcome,
          shares: p.size,
          avgPrice: p.avgPrice,
        }));
      } else if (source === "kalshi") {
        const res = await authFetch("/api/dflow/positions");
        if (!res.ok) return [];
        const data = await res.json();
        const rows = (data?.success && Array.isArray(data.data)) ? data.data : (Array.isArray(data) ? data : []);
        return rows
          .filter((p: any) => Number(p.size) > 0.001)
          .map((p: any) => ({
            tokenId: String(p.kalshiTicker || '').toUpperCase(),
            conditionId: String(p.kalshiTicker || '').toUpperCase(),  // store ticker here for matching
            marketSlug: p.kalshiTicker,
            outcome: p.outcome === 'yes' ? 'Yes' : 'No',
            shares: Number(p.size) || 0,
            avgPrice: Number(p.avgPrice) || 0,
          }));
      }
      return [];
    },
    enabled: isLoggedIn && (source === "polymarket" || source === "predict" || source === "kalshi"),
    staleTime: 30_000,
    gcTime: 60_000,
  });

  // After a trade, the modal fires positions:refresh / portfolio:refresh
  // window events. Without this listener the staleTime=30s above would
  // hold the cached "no position" state and the "You own" badge wouldn't
  // appear until either a 30s passive refetch, navigation, or hard refresh.
  // Refetch immediately + retry at 1.5s and 4s to absorb PM data-api
  // indexing lag (mirrors the MyPositions retry schedule). All three
  // fetches bypass the backend Redis cache via `bypassPositionsCacheRef`.
  useEffect(() => {
    if (!isLoggedIn) return;
    const timeouts = new Set<ReturnType<typeof setTimeout>>();
    const refetchOnce = () => {
      queryClient.invalidateQueries({ queryKey: ["user-positions-event"] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["user-open-orders-event"] }).catch(() => {});
    };
    const onRefresh = () => {
      bypassPositionsCacheRef.current = 3;
      refetchOnce();
      [1500, 4000].forEach((delay) => {
        const id = setTimeout(() => {
          timeouts.delete(id);
          refetchOnce();
        }, delay);
        timeouts.add(id);
      });
    };
    window.addEventListener("positions:refresh", onRefresh);
    window.addEventListener("portfolio:refresh", onRefresh);
    return () => {
      window.removeEventListener("positions:refresh", onRefresh);
      window.removeEventListener("portfolio:refresh", onRefresh);
      timeouts.forEach((id) => clearTimeout(id));
    };
  }, [isLoggedIn, queryClient]);

  // Fetch user open orders to show "Limit" badge (Polymarket only)
  const { data: userOpenOrders = [] } = useQuery<UserOpenOrder[]>({
    queryKey: ["user-open-orders-event"],
    queryFn: async () => {
      const res = await authFetch("/api/trade/orders");
      if (!res.ok) return [];
      const data = await res.json();
      return (data.orders || []).map((o: any) => ({
        market: o.market || '',
        asset_id: o.asset_id || '',
        side: o.side || 'BUY',
        outcome: o.outcome || (o.outcomeIndex === 0 ? 'Yes' : 'No'),
        price: o.price || 0,
        size: o.size || 0,
      }));
    },
    enabled: isLoggedIn && source === "polymarket",
    staleTime: 15_000,
    gcTime: 60_000,
  });

  // Helper to find user open orders for a market — match by tokenId (works for negRisk)
  const getMarketOrders = (market: Market): UserOpenOrder[] => {
    const tokenIds = (market as any).clobTokenIds;
    if (!tokenIds || !Array.isArray(tokenIds) || tokenIds.length === 0) return [];
    const tokenSet = new Set(tokenIds.map((t: string) => t.toLowerCase()));
    return userOpenOrders.filter((o) => o.asset_id && tokenSet.has(o.asset_id.toLowerCase()));
  };

  // Helper to find user position for a market
  const getUserPosition = (market: Market): UserPosition | null => {
    const cid = (market as any).conditionId?.toLowerCase();
    const mid = (market as any).id?.toLowerCase();  // Predict.fun uses market.id
    const tkr = source === "kalshi"
      ? String((market as any).ticker || market.slug || market.conditionId || market.id || "")
          .replace(/^ks-/i, "")
          .toUpperCase()
      : "";
    if (!cid && !mid && !tkr) return null;
    return userPositions.find((p) => {
      const pCid = p.conditionId?.toLowerCase();
      const pCidUpper = p.conditionId?.toUpperCase();
      // Match by conditionId (Polymarket), marketId (Predict.fun), or kalshi ticker
      return (
        (cid && pCid === cid) ||
        (mid && pCid === mid) ||
        (tkr && pCidUpper === tkr)
      );
    }) || null;
  };

  // Measure the table scroller's visible width so the inline expansion panel
  // can size itself to the container instead of the table's natural width.
  // Without this, colSpan=99 + overflow-x-auto + a wide tab bar (7 tabs)
  // combine to stretch the td beyond the viewport on mobile.
  //
  // CRITICAL — these hooks MUST stay above the `displayMarkets.length === 0`
  // early return below. When a Polymarket crypto up/down 5m market expires,
  // the parent's effectiveEventData.closed flips true while the underlying
  // markets[] still carries active=true, so this component's
  // `displayMarkets = markets.filter(m => m.closed)` branch returns []. If
  // the hooks lived after the early return, the render that flipped to
  // closed would skip three hooks (useRef + useState + useEffect) and React
  // would crash with #300 ("rendered fewer hooks than expected"), bringing
  // down the whole event page through the ErrorBoundary. Found 2026-04-28
  // by repro'ing in dev mode after the prod ErrorBoundary kept hiding the
  // exact component.
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [panelWidth, setPanelWidth] = useState<number | null>(null);
  useEffect(() => {
    if (!scrollerRef.current) return;
    const el = scrollerRef.current;
    const measure = () => setPanelWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Render whenever we have at least one market. Single-market events (binary
  // Polymarket deadlines, single-outcome crypto, etc.) reuse the same row →
  // expandable OutcomeDetailPanel flow so users get orderbook/trades/volume/
  // holders tabs the same way they do on multi-outcome events.
  if (displayMarkets.length === 0) return null;

  const INITIAL_LIMIT = 5;
  // Sort by endDate ascending (nearest deadline first, like Polymarket)
  // Parses date from groupItemTitle as fallback when endDate is missing (stale SSR cache)
  const parseDate = (m: Market): number => {
    if (m.endDate) return new Date(m.endDate).getTime();
    // Try parsing "March 31", "April 30", "December 31" etc from title
    const title = m.groupItemTitle || '';
    const match = title.match(/^(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2})/i);
    if (match) {
      const year = new Date().getFullYear();
      const parsed = new Date(`${match[1]} ${match[2]}, ${year}`);
      // If the date is in the past, try next year
      if (parsed.getTime() < Date.now() - 90 * 86400000) parsed.setFullYear(year + 1);
      if (!isNaN(parsed.getTime())) return parsed.getTime();
    }
    return Infinity;
  };
  // Check if all dates are the same — if so, keep backend order (e.g. Kalshi price-level events)
  const dates = displayMarkets.map(parseDate);
  const allSameDate = dates.every(d => d === dates[0]);
  const sorted = allSameDate
    ? displayMarkets
    : [...displayMarkets].sort((a, b) => parseDate(a) - parseDate(b));
  const displayed = showAllMarkets ? sorted : sorted.slice(0, INITIAL_LIMIT);
  const hiddenCount = sorted.length - INITIAL_LIMIT;
  // Single-market events (regardless of source) show "Market" rather than
  // "Outcome" in the header — there's only one row, and it's the market itself,
  // not one of several outcomes.
  const singleBinaryMarket = displayMarkets.length === 1;

  // Show-more / show-less control rendered under both layouts (mobile cards
  // + desktop table). Wrapped in a function so the JSX stays readable.
  const renderShowMoreToggle = () => {
    if ((totalMarketsCount ?? displayMarkets.length) <= 5) return null;
    return (
      <div className="px-3 py-2 border-t border-gray-200 dark:border-[#1A1A1A]">
        <button
          onClick={() => setShowAllMarkets(!showAllMarkets)}
          className="w-full py-2 text-center text-xs font-inter-tight font-semibold text-gray-600 dark:text-gray-400 bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#0f0f12] transition-colors"
        >
          {showAllMarkets ? t("eventMarkets.showLess") : t("eventMarkets.moreMarkets", { n: totalMarketsCount ?? displayMarkets.length })}
        </button>
      </div>
    );
  };

  return (
    <>
    {/* ─────────────────────────────────────────────────────────────────
        MOBILE LAYOUT (<sm) — Polymarket-style stacked cards.
        Each market is a full-width card: image + title + Vol + % chance
        on top, big Yes/No buttons in a 2-col grid underneath. Tapping
        the card body (not the buttons) opens MobileMarketDetailOverlay
        with chart / orderbook / trades / holders. The desktop accordion
        flow below is intentionally untouched — the two paths share the
        same `displayed` list but render fully independent JSX so a
        regression in one doesn't break the other.
        ─────────────────────────────────────────────────────────────── */}
    <div className="sm:hidden border-t border-gray-200 dark:border-[#1A1A1A] divide-y divide-gray-100 dark:divide-[#1A1A1A]/60">
      {displayed.map((market) => {
        const isBinary = market.outcomes.length === 2;
        const marketActive = market.active && market.acceptingOrders && !market.closed;
        const [yesPrice] = getLivePrice(market);
        const { buyYes, buyNo, hasRealBuyYes, hasRealBuyNo } = getLiveBuyPrices(market);
        const yesBuyDisabled = !marketActive || (source === 'kalshi' && hasRealBuyYes === false);
        const noBuyDisabled = !marketActive || (source === 'kalshi' && hasRealBuyNo === false);
        const isPreselectedRow = !!(selectedMarketId && selectedMarket?.id === market.id);
        const preselectedYes = isPreselectedRow && preselectedOutcome === 'yes';
        const preselectedNo = isPreselectedRow && preselectedOutcome === 'no';
        const isWorldCup = source !== 'kalshi' && eventData && isWorldCupEvent(eventData.slug, eventData.title);
        const flagUrl = (isWorldCup || (source === 'predict' && (market as any).sportsMarketType === 'moneyline')) ? getCountryFlagUrl(market.groupItemTitle) : null;
        const teamLogo = lookupTeamLogo(teamLogos, market.groupItemTitle);
        const displayImage = flagUrl || market.image || teamLogo || eventData?.image || null;
        const userPos = getUserPosition(market);
        const isClosedWinner = eventClosed && (yesPrice ?? 0) >= 0.95;
        const isClosedLoser = eventClosed && !isClosedWinner;
        const yesAbbr = getOutcomeAbbr(typeof market.outcomes?.[0] === 'string' ? market.outcomes[0] : (market.outcomes?.[0] as any)?.name || 'Yes', eventData.slug, eventData.title);
        const noAbbr = getOutcomeAbbr(typeof market.outcomes?.[1] === 'string' ? market.outcomes[1] : (market.outcomes?.[1] as any)?.name || 'No', eventData.slug, eventData.title);

        const openDetail = () => {
          if (eventClosed) return;
          if (onSelectMarketMobile) {
            onSelectMarketMobile(market.slug || market.id);
          } else {
            // Fallback to the existing accordion flow when the parent
            // hasn't wired up the overlay yet — keeps the mobile layout
            // usable in isolation (e.g. during incremental rollout).
            onSelectMarket(market.slug || market.id);
          }
        };

        return (
          <div
            key={market.id}
            className={`px-4 py-4 ${isClosedLoser ? 'opacity-60' : ''} ${isClosedWinner ? 'bg-emerald-500/[0.04]' : ''}`}
          >
            {/* Header — image + title + % on the right.
                The whole header acts as the tap target that opens the
                detail overlay; the buttons stop propagation so they
                don't double-fire. */}
            <button
              type="button"
              onClick={openDetail}
              disabled={eventClosed}
              className="w-full flex items-start gap-3 text-left disabled:cursor-default"
            >
              {(market.imageUrl || (market as any).image || displayImage) && (
                <div className="relative w-10 h-10 rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0f0f12] flex-shrink-0 mt-0.5">
                  <CdnImage
                    src={market.imageUrl || (market as any).image || displayImage || ''}
                    alt={market.question}
                    fill
                    className="object-cover"
                    loading="lazy"
                    // 2026-05-26: fall through to the parent event image when
                    // the per-market URL fails. Polymarket's _next/image
                    // optimizer cold-POPs on rarely-viewed S3 entries (see
                    // CdnImage.tsx:74-80) and was leaving every row showing
                    // a broken-image "?" placeholder on Pika's mobile screen
                    // when bulk-loading the markets grid over cellular.
                    fallbackSrc={displayImage || undefined}
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <span className="font-satoshi font-semibold text-[15px] text-gray-900 dark:text-white leading-snug line-clamp-2">
                      {market.groupItemTitle || market.question}
                    </span>
                    <div className="flex items-center gap-2 mt-1 text-[12px] text-gray-500 dark:text-gray-400">
                      <span className="tabular-nums">{formatVolume(market.volume)} {t("eventGrid.volAbbr")}</span>
                      {userPos && userPos.shares > 0 && (
                        <span
                          className={`inline-flex items-center gap-1 h-[16px] px-1.5 rounded text-[9px] font-inter-tight font-semibold whitespace-nowrap tabular-nums ${
                            userPos.outcome.toLowerCase() === 'yes'
                              ? 'bg-[#00FF9D]/10 text-[#00FF9D]'
                              : 'bg-[#FF1744]/10 text-[#FF1744]'
                          }`}
                        >
                          {userPos.outcome.toUpperCase()} {userPos.shares.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </div>
                  {!eventClosed && (
                    <span suppressHydrationWarning className="text-2xl font-satoshi font-bold text-gray-900 dark:text-white tabular-nums shrink-0">
                      {formatPriceCents(yesPrice ?? 0, market.decimalPrecision)}%
                    </span>
                  )}
                  {eventClosed && (
                    <span className={`text-sm font-inter-tight font-bold shrink-0 ${
                      isClosedWinner ? 'text-[#00FF9D]' : 'text-[#FF1744]'
                    }`}>
                      {isClosedWinner ? (market.outcomes?.[0] || 'Yes') : (market.outcomes?.[1] || 'No')}
                    </span>
                  )}
                </div>
              </div>
            </button>

            {/* Yes / No buttons — only for binary active markets. Big,
                full-width pair so the price + label is the dominant CTA
                (Polymarket parity).  */}
            {!eventClosed && isBinary && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (yesBuyDisabled) return;
                    if (onSelectOutcome) {
                      onSelectOutcome(market.slug || market.id, 0);
                    } else {
                      openTradingModal({
                        isOpen: true,
                        marketSlug: market.slug,
                        outcome: 'YES',
                        outcomePrice: buyYes,
                        marketQuestion: market.question,
                        actualOutcome: market.outcomes?.[0],
                        outcomes: market.outcomes,
                      });
                    }
                  }}
                  disabled={yesBuyDisabled}
                  className={`py-3 rounded-[3px] border font-inter-tight font-bold text-sm tabular-nums transition-colors ${
                    preselectedYes
                      ? 'bg-emerald-600 text-white border-transparent dark:bg-[#00FF9D] dark:text-black'
                      : 'bg-emerald-500/10 dark:bg-[#00FF9D]/[0.12] text-emerald-600 dark:text-[#00FF9D] border-emerald-500/25 dark:border-[#00FF9D]/25 hover:bg-emerald-500/[0.18] dark:hover:bg-[#00FF9D]/[0.18]'
                  } ${yesBuyDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {yesAbbr} {formatPriceCents(buyYes ?? 0, market.decimalPrecision)}¢
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (noBuyDisabled) return;
                    if (onSelectOutcome) {
                      onSelectOutcome(market.slug || market.id, 1);
                    } else {
                      openTradingModal({
                        isOpen: true,
                        marketSlug: market.slug,
                        outcome: 'NO',
                        outcomePrice: buyNo,
                        marketQuestion: market.question,
                        actualOutcome: market.outcomes?.[1],
                        outcomes: market.outcomes,
                      });
                    }
                  }}
                  disabled={noBuyDisabled}
                  className={`py-3 rounded-[3px] border font-inter-tight font-bold text-sm tabular-nums transition-colors ${
                    preselectedNo
                      ? 'bg-red-600 text-white border-transparent dark:bg-[#FF1744]'
                      : 'bg-red-500/10 dark:bg-[#FF1744]/[0.12] text-red-600 dark:text-[#FF1744] border-red-500/25 dark:border-[#FF1744]/25 hover:bg-red-500/[0.18] dark:hover:bg-[#FF1744]/[0.18]'
                  } ${noBuyDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {noAbbr} {formatPriceCents(buyNo ?? 0, market.decimalPrecision)}¢
                </button>
              </div>
            )}
          </div>
        );
      })}
      {renderShowMoreToggle()}
    </div>

    {/* ─────────────────────────────────────────────────────────────────
        DESKTOP LAYOUT (sm+) — original table + inline accordion.
        Untouched from before the mobile rework.
        ─────────────────────────────────────────────────────────────── */}
    <div className="hidden sm:block border-t border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
      <div ref={scrollerRef} className="overflow-x-auto">
        <table className="w-full sm:min-w-[560px]">
          <thead className="bg-gray-50 dark:bg-[#0f0f12] border-b border-gray-200 dark:border-[#1A1A1A] sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {singleBinaryMarket ? t("marketsGrid.market") : t("marketsGrid.outcome")}
              </th>
              {!eventClosed && (
                <>
                  <th className="px-3 py-2 text-center text-[10px] font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                    {t("marketsGrid.chance")}
                  </th>
                </>
              )}
              <th className="px-3 py-2 text-right text-[10px] font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("marketsGrid.volume")}
              </th>
              {/* 24h volume — only when the source actually supplies it
                  (Polymarket today). Hidden on phones to keep the row
                  scannable; lg+ surfaces it next to total volume. */}
              {source === 'polymarket' && (
                <th className="px-3 py-2 text-right text-[10px] font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  {t("marketsGrid.volume24h")}
                </th>
              )}
              {!eventClosed && (
                <>
                  <th className="px-3 py-2 text-right text-[10px] font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                    {source === 'kalshi' ? t("common.openInterest") : t("trading.liquidity")}
                  </th>
                  <th className="px-3 py-2 text-center text-[10px] font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t("marketsGrid.buy")}
                  </th>
                </>
              )}
              {eventClosed && (
                <th className="px-3 py-2 text-center text-[10px] font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("marketsGrid.result")}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-[#1A1A1A]/50">
            {displayed.map((market, index) => {
              const isBinary = market.outcomes.length === 2;
              const marketActive = market.active && market.acceptingOrders && !market.closed;
              // Use real-time prices - lastTradePrice for % CHANCE, orderbook prices for buttons
              const [yesPrice] = getLivePrice(market);
              const { buyYes, buyNo, hasRealBuyYes, hasRealBuyNo } = getLiveBuyPrices(market);
              // When the live orderbook has no ask on a side, buyYes/buyNo
              // fall back to the bid (which is the price someone wants to
              // PAY, not what's offered to a buyer). The trade would route
              // to DFlow and fail with route_not_found → the UI we showed
              // was misleading. Disable the button up-front so users
              // understand the side isn't available right now.
              const yesBuyDisabled = !marketActive || (source === 'kalshi' && hasRealBuyYes === false);
              const noBuyDisabled = !marketActive || (source === 'kalshi' && hasRealBuyNo === false);
              // Is this the row that matches the URL preselection? Used to highlight the YES/NO
              // button that came in via ?outcome=… so the user can see which side was clicked.
              const isPreselectedRow = !!(selectedMarketId && selectedMarket?.id === market.id);
              const preselectedYes = isPreselectedRow && preselectedOutcome === 'yes';
              const preselectedNo = isPreselectedRow && preselectedOutcome === 'no';
              // Use country flags for World Cup events, event image as fallback for all sources
              const isWorldCup = source !== 'kalshi' && eventData && isWorldCupEvent(eventData.slug, eventData.title);
              const flagUrl = (isWorldCup || (source === 'predict' && (market as any).sportsMarketType === 'moneyline')) ? getCountryFlagUrl(market.groupItemTitle) : null;
              // Team logo lookup (works for any sport in our shared logos map — used by Kalshi
              // sports outcomes which don't ship per-market images from the backend).
              const teamLogo = lookupTeamLogo(teamLogos, market.groupItemTitle);
              const displayImage = flagUrl || market.image || teamLogo || eventData?.image || null;

              // User position badge + open orders
              const userPos = getUserPosition(market);
              const marketOrders = getMarketOrders(market);

              // Accordion identity — the row onClick passes `market.slug || market.id`
              // upstream, so `expandedMarketId` can hold either. Compare against
              // both to avoid the "click doesn't expand" regression when slug !== id.
              // If the user explicitly collapsed this specific row, skip the
              // selectedMarketId fallback so the accordion stays closed.
              const isUserCollapsedRow = userCollapsedMarketId != null && (
                userCollapsedMarketId === market.id || userCollapsedMarketId === market.slug
              );
              const activeExpandedId = expandedMarketId ?? (isUserCollapsedRow ? null : selectedMarketId);
              const isRowExpanded = activeExpandedId != null && (
                activeExpandedId === market.id || activeExpandedId === market.slug
              );

              const isClosedWinner = eventClosed && (yesPrice ?? 0) >= 0.95;
              const isClosedLoser = eventClosed && !isClosedWinner;
              // Gift rewards icon — rendered to the RIGHT of the outcome name
              // (moved out of the info row 2026-06-09). Desktop-only, polymarket
              // markets with an active liquidity-rewards pool.
              const giftNode = (!eventClosed && source === 'polymarket') ? (() => {
                const cid = ((market as any).conditionId || '').toLowerCase();
                const rewards = rewardsMap?.[cid];
                if (!rewards || rewards.ratePerDay <= 0) return null;
                const tooltipContent = (
                  <div className="min-w-[220px] text-left p-3 space-y-2">
                    <p className="text-[11px] leading-snug text-gray-300">
                      {t("marketsGrid.rewards.tooltip")}
                    </p>
                    <div className="pt-2 border-t border-white/10 space-y-1.5">
                      <div className="flex justify-between items-center">
                        <span className="text-[11px] text-gray-400">{t("marketsGrid.rewards.daily")}</span>
                        <span className="text-[11px] font-semibold text-white">
                          ${Math.round(rewards.ratePerDay)}<span className="text-gray-400 font-normal">{t("marketsGrid.rewards.dailySuffix")}</span>
                        </span>
                      </div>
                      {rewards.maxSpread > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-[11px] text-gray-400">{t("marketsGrid.rewards.maxSpread")}</span>
                          <span className="text-[11px] font-semibold text-white">
                            ±{Math.floor(rewards.maxSpread)}¢
                          </span>
                        </div>
                      )}
                      {rewards.minSize > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-[11px] text-gray-400">{t("marketsGrid.rewards.minShares")}</span>
                          <span className="text-[11px] font-semibold text-white">{rewards.minSize}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
                return (
                  <Tooltip content={tooltipContent} maxWidth={260}>
                    <span className="hidden sm:inline-flex items-center text-gray-500 hover:text-gray-300 transition-colors" aria-label={t("marketsGrid.rewards.aria")}>
                      <Gift size={12} />
                    </span>
                  </Tooltip>
                );
              })() : null;
              return (
                <Fragment key={market.id}>
                <tr
                  className={`transition-colors group animate-stagger-in ${
                    eventClosed
                      ? `cursor-default ${
                          isClosedWinner
                            ? 'bg-emerald-50/60 dark:bg-emerald-500/[0.04] border-l-2 border-l-emerald-500'
                            : isClosedLoser
                            ? 'opacity-60'
                            : ''
                        }`
                      : 'cursor-pointer ' + (selectedMarketId && selectedMarket?.id === market.id
                          ? 'bg-gray-100 dark:bg-[#26262D] border-l-2 border-l-gray-300 dark:border-l-zinc-600'
                          : 'hover:bg-gray-50 dark:hover:bg-[#0f0f12]/30')
                  }`}
                  style={{ animationDelay: `${index * 30}ms` }}
                  // Closed events are read-only — no row click, no expand,
                  // and the YES/NO buttons further down are already gated
                  // off by the {!eventClosed && ...} branches.
                  onClick={eventClosed ? undefined : () => onSelectMarket(market.slug || market.id)}
                >
                  {/* Outcome with Image */}
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                      {/* Image + position badge column */}
                      <div className="flex flex-col items-center gap-1 flex-shrink-0">
                        {(market.imageUrl || (market as any).image || displayImage) && (
                          <div className="relative w-8 h-8 rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] group-hover:border-gray-400 dark:group-hover:border-gray-500 transition-colors">
                            <CdnImage
                              src={market.imageUrl || (market as any).image || displayImage || ""}
                              alt={market.question}
                              fill
                              className="object-cover"
                              loading="lazy"
                              // 2026-05-26: replaced an `onError` that mutated
                              // img.src directly — Next.js Image owns the
                              // element's src and overwrites manual changes
                              // on the next render. `fallbackSrc` is the
                              // documented prop that swaps via React state.
                              fallbackSrc={displayImage || undefined}
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-satoshi font-semibold text-sm text-gray-900 dark:text-white line-clamp-2 sm:line-clamp-1 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                            {market.groupItemTitle || market.question}
                          </span>
                          {/* Inline metadata pills — moved here from the image
                              column so they read as market context instead of
                              stacking awkwardly under the 32px avatar. */}
                          {userPos && userPos.shares > 0 && (
                            <span
                              className={`hidden sm:inline-flex items-center gap-1 h-[18px] px-1.5 rounded text-[9px] font-inter-tight font-semibold whitespace-nowrap tabular-nums ${
                                userPos.outcome.toLowerCase() === 'yes'
                                  ? 'bg-[#00FF9D]/10 text-[#00FF9D]'
                                  : 'bg-[#FF1744]/10 text-[#FF1744]'
                              }`}
                              title={`You own ${(userPos.shares ?? 0).toFixed(2)} ${userPos.outcome} shares @ avg ${((userPos.avgPrice ?? 0) * 100).toFixed(1)}¢`}
                            >
                              <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor"><circle cx="4" cy="4" r="3" /></svg>
                              {userPos.outcome.toUpperCase()} {(userPos.shares ?? 0).toFixed(1)}
                            </span>
                          )}
                          {marketOrders.length > 0 && (
                            <span
                              className="hidden sm:inline-flex items-center gap-1 h-[18px] px-1.5 rounded text-[9px] font-inter-tight font-semibold whitespace-nowrap bg-amber-500/10 text-amber-600 dark:text-amber-400"
                              title={`${marketOrders.length} open limit order${marketOrders.length > 1 ? 's' : ''}`}
                            >
                              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M1 4h6M4 1v6" /></svg>
                              {marketOrders.length} LIMIT
                            </span>
                          )}
                          {giftNode}
                        </div>
                        {/* Mobile: Show chance inline (active events only).
                            Change (1h/24h) lives only in the desktop Change
                            column — mobile keeps just the chance % to reduce
                            clutter. */}
                        {!eventClosed && (
                          <div className="sm:hidden flex items-center gap-2 mt-0.5">
                            <span suppressHydrationWarning className="text-xs font-inter-tight font-bold text-gray-900 dark:text-white">
                              {formatPriceCents(yesPrice ?? 0, market.decimalPrecision)}%
                            </span>
                          </div>
                        )}
                        {/* Info row: Spread | Friends | Gift (active events only) */}
                        {!eventClosed && (
                          <div className="hidden sm:flex items-center gap-2 mt-0.5">
                            {/* Spread bar removed 2026-06-09 — too technical for
                                every row by default (like the per-row mini-charts). */}
                            {(() => {
                              const traders = getTradersForMarket(networkActivity, market.slug);
                              return traders.length > 0 ? <MarketNetworkAvatars traders={traders} /> : null;
                            })()}
                          </div>
                        )}
                                              </div>
                    </div>
                  </td>

                  {/* Chance - Desktop (active events only) */}
                  {!eventClosed && (
                    <td className="px-3 py-2.5 text-center align-middle hidden sm:table-cell">
                      <span suppressHydrationWarning className="text-base font-inter-tight font-bold text-gray-900 dark:text-white">
                        {formatPriceCents(yesPrice ?? 0, market.decimalPrecision)}%
                      </span>
                    </td>
                  )}


                  {/* Volume */}
                  <td className="px-3 py-2.5 text-right align-middle">
                    <span className="text-xs font-inter-tight font-medium text-gray-600 dark:text-gray-400">
                      {formatVolume(market.volume)}
                    </span>
                  </td>

                  {/* 24h Volume — paired with the conditional header above.
                      Render `—` when the field is missing rather than 0
                      so a brand-new market doesn't display "$0" and look
                      like an outage. */}
                  {source === 'polymarket' && (
                    <td className="px-3 py-2.5 text-right align-middle hidden lg:table-cell">
                      <span className="text-xs font-inter-tight font-medium text-gray-600 dark:text-gray-400">
                        {market.volume24hr != null && market.volume24hr > 0
                          ? formatVolume(market.volume24hr)
                          : <span className="text-gray-400 dark:text-gray-600">—</span>}
                      </span>
                    </td>
                  )}

                  {/* Liquidity / Open Interest (active events only) */}
                  {!eventClosed && (
                    <td className="px-3 py-2.5 text-right align-middle hidden lg:table-cell">
                      <span className="text-xs font-inter-tight font-medium text-gray-600 dark:text-gray-400">
                        {formatVolume(source === 'kalshi' && market.openInterest ? market.openInterest : market.liquidity)}
                      </span>
                    </td>
                  )}

                  {/* Buy Buttons (active events only) */}
                  {!eventClosed && (
                    <td className="px-3 py-2.5 align-middle">
                      {isBinary && (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (yesBuyDisabled) return;
                              // Event page: route to sidebar TradingPanel
                              // (non-blocking). Fallback to modal for contexts
                              // without a sidebar (shouldn't happen on events).
                              if (onSelectOutcome) {
                                onSelectOutcome(market.slug || market.id, 0);
                              } else {
                                openTradingModal({
                                  isOpen: true,
                                  marketSlug: market.slug,
                                  outcome: "YES",
                                  outcomePrice: buyYes,
                                  marketQuestion: market.question,
                                  actualOutcome: market.outcomes?.[0],
                                  outcomes: market.outcomes,
                                });
                              }
                            }}
                            disabled={yesBuyDisabled}
                            title={
                              source === 'kalshi' && hasRealBuyYes === false
                                ? "No asks available right now — try another strike or wait"
                                : undefined
                            }
                            className={`flex items-center justify-center gap-1 min-w-[72px] px-2 py-1.5 rounded-[3px] whitespace-nowrap transition-colors ${
                              preselectedYes
                                ? "bg-emerald-600 dark:bg-[#00FF9D] hover:bg-emerald-700 dark:hover:brightness-95"
                                : "bg-emerald-500/10 dark:bg-[#00FF9D]/[0.12] hover:bg-emerald-500/[0.18] dark:hover:bg-[#00FF9D]/[0.18]"
                            } ${yesBuyDisabled && "opacity-50 cursor-not-allowed"}`}
                          >
                            <span className={`text-[10px] font-inter-tight font-bold uppercase ${preselectedYes ? "text-white/80 dark:text-black/70" : "text-emerald-600/90 dark:text-[#00FF9D]/80"}`}>{getOutcomeAbbr(typeof market.outcomes?.[0] === 'string' ? market.outcomes[0] : (market.outcomes?.[0] as any)?.name || 'Yes', eventData.slug, eventData.title)}</span>
                            <span className={`text-sm font-inter-tight font-bold ${preselectedYes ? "text-white dark:text-black" : "text-emerald-600 dark:text-[#00FF9D]"}`}>
                              {formatPriceCents(buyYes ?? 0, market.decimalPrecision)}¢
                            </span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (noBuyDisabled) return;
                              if (onSelectOutcome) {
                                onSelectOutcome(market.slug || market.id, 1);
                              } else {
                                openTradingModal({
                                  isOpen: true,
                                  marketSlug: market.slug,
                                  outcome: "NO",
                                  outcomePrice: buyNo,
                                  marketQuestion: market.question,
                                  actualOutcome: market.outcomes?.[1],
                                  outcomes: market.outcomes,
                                });
                              }
                            }}
                            disabled={noBuyDisabled}
                            title={
                              source === 'kalshi' && hasRealBuyNo === false
                                ? "No asks available right now — try another strike or wait"
                                : undefined
                            }
                            className={`flex items-center justify-center gap-1 min-w-[72px] px-2 py-1.5 rounded-[3px] whitespace-nowrap transition-colors ${
                              preselectedNo
                                ? "bg-red-600 dark:bg-[#FF1744] hover:bg-red-700 dark:hover:brightness-95"
                                : "bg-red-500/10 dark:bg-[#FF1744]/[0.12] hover:bg-red-500/[0.18] dark:hover:bg-[#FF1744]/[0.18]"
                            } ${noBuyDisabled && "opacity-50 cursor-not-allowed"}`}
                          >
                            <span className={`text-[10px] font-inter-tight font-bold uppercase ${preselectedNo ? "text-white/80" : "text-red-600/90 dark:text-[#FF1744]/80"}`}>{getOutcomeAbbr(typeof market.outcomes?.[1] === 'string' ? market.outcomes[1] : (market.outcomes?.[1] as any)?.name || 'No', eventData.slug, eventData.title)}</span>
                            <span className={`text-sm font-inter-tight font-bold ${preselectedNo ? "text-white" : "text-red-600 dark:text-[#FF1744]"}`}>
                              {formatPriceCents(buyNo ?? 0, market.decimalPrecision)}¢
                            </span>
                          </button>
                        </div>
                      )}
                    </td>
                  )}

                  {/* Result indicator (closed events only).
                      Label uses the market's actual outcomes array so
                      crypto updown rows resolve as "Up"/"Down" instead
                      of being hardcoded to "Yes"/"No" (which made
                      `btc-updown-5m` markets show "Result: No" when the
                      DOWN side won — accurate price-wise, misleading
                      to read). Falls back to Yes/No when the outcomes
                      pair is missing or empty. */}
                  {eventClosed && (
                    <td className="px-3 py-2.5 align-middle text-center">
                      {(() => {
                        // For resolved markets, price is 1.0 (100%) for winner, 0 for loser
                        const isWinner = (yesPrice ?? 0) >= 0.95;
                        const winnerLabel = market.outcomes?.[0] || 'Yes';
                        const loserLabel = market.outcomes?.[1] || 'No';
                        return isWinner ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-[5px] bg-[#00FF9D]/10 text-[#00FF9D]">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-xs font-inter-tight font-bold">{winnerLabel}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-[5px] bg-[#FF1744]/10 text-[#FF1744]">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span className="text-xs font-inter-tight font-bold">{loserLabel}</span>
                          </span>
                        );
                      })()}
                    </td>
                  )}
                </tr>
                {/* Expanded row — ACTIVE events: Chart/Orderbook/Trades/Top
                    Holders/Smart Money tabs inline under the clicked outcome.
                    Clicking the same row again toggles it off (handled upstream
                    by onSelectMarket). Closed events get the simpler chart-only
                    row further down. */}
                {/* Expanded panel — inline accordion under the clicked row.
                    The td has width:0 forced + position:relative, and the
                    inner div is absolutely positioned with a measured width
                    (panelWidth from the scroller). This way colSpan=99 never
                    stretches the natural column widths. */}
                {!eventClosed && isRowExpanded && panelWidth != null && (
                  <tr
                    className="bg-gray-50 dark:bg-[#1A1A1A] border-l-2 border-l-gray-300 dark:border-l-zinc-600"
                  >
                    <td
                      colSpan={99}
                      style={{ position: "relative", padding: 0 }}
                    >
                      <div style={{ width: panelWidth }}>
                        <OutcomeDetailPanel
                          selectedMarket={market}
                          source={source}
                          kalshiTicker={
                            source === "kalshi"
                              ? String(
                                  (market as any).ticker
                                  || market.slug
                                  || market.conditionId
                                  || market.id
                                  || ""
                                ).replace(/^ks-/i, "").toUpperCase()
                              : undefined
                          }
                          yesPrice={buyYes}
                          noPrice={buyNo}
                          hideAdvancedTabs={hideAdvancedTabs}
                        />
                      </div>
                    </td>
                  </tr>
                )}
                {/* Expanded row for closed events when this market is selected — only show if chart data exists */}
                {eventClosed && selectedMarketId && selectedMarket?.id === market.id && market.clobTokenIds && market.clobTokenIds.length >= 2 && (
                  <tr className="bg-gray-50/50 dark:bg-[#0a0a0b]/50">
                    <td colSpan={3} className="px-3 py-4">
                      <div className="rounded-[5px] overflow-hidden border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D]">
                        <MarketChart
                          marketSlug={market.slug}
                          clobTokenIds={market.clobTokenIds}
                          outcomes={market.outcomes || ['Yes', 'No']}
                          conditionId={market.conditionId}
                          marketEnded={true}
                          leftAction={
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-monument font-bold ${
                                (yesPrice ?? 0) >= 0.95 ? 'text-[#00FF9D]' : 'text-[#FF1744]'
                              }`}>
                                {(yesPrice ?? 0) >= 0.99 ? '>99%' : (yesPrice ?? 0) <= 0.01 ? '<1%' : `${Math.round((yesPrice ?? 0) * 100)}%`}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">{t("market.chance")}</span>
                            </div>
                          }
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {renderShowMoreToggle()}
    </div>
    </>
  );
}
